/**
 * The resumable step model (pure — no React, fully unit-testable).
 *
 * "The link is the session": a returning referee lands on their furthest incomplete step, and the
 * guards stop them jumping ahead. Both facts derive from the server `status` alone, so this module
 * is the single source of truth for step ↔ status and is exercised directly by the tests.
 */
import type { ReferenceStatus } from '@/lib/api-types';

/** Ordered, linear in-flow steps the referee walks (ux-flows §2 steps 1–9). */
export const FLOW_STEPS = ['landing', 'confirm', 'record', 'processing', 'review', 'sign', 'done'] as const;
export type FlowStep = (typeof FLOW_STEPS)[number];

/** Calm paper terminal/hold pages that sit outside the linear flow (§5 + step 2). */
export const TERMINAL_STEPS = ['declined', 'expired', 'withdrawn', 'under-review', 'not-found'] as const;
export type TerminalStep = (typeof TERMINAL_STEPS)[number];

export type RefereeStep = FlowStep | TerminalStep;

export function isFlowStep(step: RefereeStep): step is FlowStep {
  return (FLOW_STEPS as readonly string[]).includes(step);
}

/** Zero-based position of a flow step (terminals return -1). */
export function stepIndex(step: RefereeStep): number {
  return isFlowStep(step) ? FLOW_STEPS.indexOf(step) : -1;
}

/**
 * The canonical "restore-to" step for a status — where TokenEntry lands the referee and where a
 * blocked navigation redirects. `rerecord_requested` sends them back to Record (they must speak
 * again); the "we'll email you" hold is `needs_review`; `drafted` restores to Review (not Sign).
 */
export function statusToStep(status: ReferenceStatus): RefereeStep {
  switch (status) {
    case 'invited':
      return 'landing';
    case 'accepted':
      return 'confirm';
    case 'details_confirmed':
    case 'rerecord_requested':
      return 'record';
    case 'recorded':
    case 'processing':
      return 'processing';
    case 'drafted':
      return 'review';
    case 'signed':
      return 'done';
    case 'declined':
      return 'declined';
    case 'expired':
      return 'expired';
    case 'withdrawn':
      return 'withdrawn';
    case 'needs_review':
    case 'failed':
      return 'under-review';
  }
}

/**
 * The furthest flow step the referee may OPEN for a status. Usually the same as the restore step,
 * with two exceptions where a client sub-step has no backend status of its own:
 *  - `drafted` also unlocks Sign — Sign is the action that ends `drafted`, so Review→Sign must be
 *    reachable while still `drafted`.
 *  - `accepted` also unlocks Record — confirm-details and consent are client sub-steps that do NOT
 *    advance the server status (it stays `accepted` until audio is recorded, WP-B11 contract), so
 *    Confirm→Record must be reachable while still `accepted`. (The mock also seeds a distinct
 *    `details_confirmed` for demoing straight into Record; the real backend never emits it.)
 */
export function furthestStepFor(status: ReferenceStatus): RefereeStep {
  if (status === 'drafted') return 'sign';
  if (status === 'accepted') return 'record';
  return statusToStep(status);
}

/** Is this status one the referee can no longer act on (terminal or an email-return hold)? */
export function isTerminalStatus(status: ReferenceStatus): boolean {
  const step = statusToStep(status);
  return !isFlowStep(step) || step === 'done';
}

/**
 * Can the referee view `target` given the server `status`?
 *  - Terminal statuses lock to exactly their page (no wandering the flow).
 *  - In-flow: any step at or before the furthest step is viewable (they may go back to re-read a
 *    prior screen), but never one ahead.
 */
export function canAccess(status: ReferenceStatus, target: RefereeStep): boolean {
  // Terminal statuses (incl. `signed`→Done and the calm §5 holds) lock to exactly their page —
  // once signed there is no going back to Review, and a declined/expired link never opens the flow.
  if (isTerminalStatus(status)) return target === statusToStep(status);
  if (!isFlowStep(target)) return false; // in-flow status can't open a terminal page
  return stepIndex(target) <= stepIndex(furthestStepFor(status));
}

/** Where a mis-targeted navigation should be redirected to for this status. */
export function redirectStepFor(status: ReferenceStatus): RefereeStep {
  return statusToStep(status);
}
